import React, {useContext, useEffect, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import {
  Collapse,
  Divider,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  RootRef,
  Typography,
  withStyles,
} from '@material-ui/core'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import ExpandLessIcon from '@material-ui/icons/ExpandLess'
import DeleteIcon from '@material-ui/icons/Delete'
import EditIcon from '@material-ui/icons/Edit'
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined'
import MoreHorizIcon from '@material-ui/icons/MoreHoriz'

import InfoTooltip from '../../../common/InfoTooltip'

import {currencyFormat} from '../../../utils/MoneyUtils'

import styles from './styles'
import NewTestErrorContext from '../../context/NewTestErrorContext'

const AudienceGroupCard = ({
  classes,
  group,
  editGroup,
  duplicateGroup,
  deleteGroup,
  showGroupCost,
  shouldScrollIntoView,
  onScrolledIntoView,
}) => {
  const {audienceGroupErrors} = useContext(NewTestErrorContext)

  const [isCollapsed, setIsCollapsed] = useState(false)
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [menuAnchor, setMenuAnchor] = useState(null)
  const [isErrorTooltipOpen, setIsErrorTooltipOpen] = useState(false)

  const cardRef = useRef(null)

  const hasError = audienceGroupErrors[group.id]

  // a freshly duplicated card is often below the fold of the groups
  // container (maxHeight: 60vh), which would make Duplicate Group read as
  // doing nothing; nothing is opened, focused or expanded.
  // The nearest scrollable ancestor is scrolled directly rather than via
  // scrollIntoView, which also walks the page and would yank the order
  // summary's publish button out of view when duplicating from that step
  useEffect(() => {
    if (!shouldScrollIntoView || !cardRef.current) return

    const card = cardRef.current
    const container = card.parentElement

    if (container && container.scrollHeight > container.clientHeight) {
      // both rects are viewport-relative, so the two sides of each subtraction
      // are in the same coordinate space. Do NOT reach for `offsetTop` here:
      // it is measured from the nearest positioned ancestor, which is never
      // this container (it declares no `position`) — the mismatch silently
      // inflates the overflow and jumps the list to its end.
      const cardRect = card.getBoundingClientRect()
      const containerRect = container.getBoundingClientRect()

      const overflowBelow = cardRect.bottom - containerRect.bottom
      const overflowAbove = containerRect.top - cardRect.top

      if (overflowBelow > 0) {
        container.scrollTop += overflowBelow
      } else if (overflowAbove > 0) {
        container.scrollTop -= overflowAbove
      }
    }

    onScrolledIntoView()
  }, [shouldScrollIntoView])

  const openMenu = anchor => {
    setMenuAnchor(anchor)
    setIsMenuOpen(true)
  }

  const closeMenu = () => {
    setMenuAnchor(null)
    setIsMenuOpen(false)
  }

  const handleMenuClick = e => {
    e.stopPropagation()
    openMenu(e.currentTarget)
  }

  const handleMenuClose = e => {
    e.stopPropagation()
    closeMenu()
  }

  const handleGroupDeletion = e => {
    e.stopPropagation()
    deleteGroup()
    closeMenu()
  }

  const handleGroupEdit = e => {
    e.stopPropagation()
    editGroup()
    closeMenu()
  }

  const handleGroupDuplication = e => {
    e.stopPropagation()
    duplicateGroup()
    closeMenu()
  }

  const getSubDemographicLabels = demographic => {
    const result = []

    for (let i = 0; i < demographic.subDemographics.length; i++) {
      const subDemographic = demographic.subDemographics[i]

      result.push(subDemographic.displayName)
    }

    return result.join(', ')
  }

  return (
    <RootRef rootRef={cardRef}>
      <Grid className={classes.card}>
        <Grid
          className={clsx(classes.header, hasError ? classes.audienceCardError : '')}
          onClick={() => setIsCollapsed(!isCollapsed)}
        >
          <Grid className={classes.collapseIcon}>
            {isCollapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
          </Grid>

          <Typography className={classes.groupName}>{group.name}</Typography>

          <IconButton classes={{root: classes.menuButton}} onClick={handleMenuClick}>
            <MoreHorizIcon className={classes.menuIcon} />
          </IconButton>

          <Menu
            open={isMenuOpen}
            onClose={handleMenuClose}
            anchorEl={menuAnchor}
            PaperProps={{
              style: {
                transform: 'translateX(10px) translateY(50px)',
              },
            }}
          >
            <MenuItem onClick={handleGroupEdit}>
              <EditIcon className={`${classes.actionIcon} ${classes.editIcon}`} />
              View/Edit
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleGroupDuplication}>
              <FileCopyOutlinedIcon className={`${classes.actionIcon} ${classes.duplicateIcon}`} />
              Duplicate Group
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleGroupDeletion}>
              <DeleteIcon className={`${classes.actionIcon} ${classes.deleteIcon}`} />
              Delete
            </MenuItem>
          </Menu>
        </Grid>

        <Collapse in={!isCollapsed}>
          <Grid className={clsx(classes.body, hasError ? classes.audienceCardBodyError : '')}>
            {group.demographics.map((demographic, index) => (
              <Grid key={index} className={classes.demographicDetails}>
                <Typography className={classes.demographicLabel}>{demographic.label}:</Typography>
                <Typography className={classes.subDemographics}>
                  {getSubDemographicLabels(demographic)}
                </Typography>
              </Grid>
            ))}
            {showGroupCost && (
              <Grid className={`${classes.demographicDetails} ${classes.groupCostDetails}`}>
                <Typography className={classes.demographicLabel}>Cost:</Typography>
                <Typography className={classes.subDemographics}>
                  {currencyFormat(group.groupCost)}
                </Typography>
              </Grid>
            )}
          </Grid>
        </Collapse>

        {hasError ? (
          <Grid className={classes.audienceCardErrorContainer}>
            <Typography style={{color: 'white', marginRight: 8, fontWeight: 600}}>
              Please Edit Selections
            </Typography>
            <InfoTooltip
              text={
                <span>
                  Unfortunately, your current selections can not be fulfilled. Please refine your
                  selections and/or change days in field to ensure delivery.
                </span>
              }
              placement="right"
              open={isErrorTooltipOpen}
              iconProps={{style: {position: 'initial', color: 'white'}}}
              onOpen={() => setIsErrorTooltipOpen(true)}
              onClose={() => setIsErrorTooltipOpen(false)}
            />
          </Grid>
        ) : (
          <Grid className={classes.panelistsContainer}>
            <Typography>{group.totalCompletes} Panelists</Typography>
          </Grid>
        )}
      </Grid>
    </RootRef>
  )
}

AudienceGroupCard.defaultProps = {
  showGroupCost: false,
  shouldScrollIntoView: false,
  onScrolledIntoView: () => {},
}

AudienceGroupCard.propTypes = {
  group: PropTypes.objectOf(PropTypes.any).isRequired,
  editGroup: PropTypes.func.isRequired,
  duplicateGroup: PropTypes.func.isRequired,
  deleteGroup: PropTypes.func.isRequired,
  showGroupCost: PropTypes.bool,
  shouldScrollIntoView: PropTypes.bool,
  onScrolledIntoView: PropTypes.func,
}

export default withStyles(styles)(AudienceGroupCard)
